/**
 * Centralized feature flag registry with type definitions.
 *
 * All feature flags should be registered here with their type and description.
 * This enables type-safe flag consumption via useTypedFlag().
 *
 * @see specs/feature-flags-system.md
 */

import {
  useFeatureFlag,
  type UseFeatureFlagOptions,
  type UseFeatureFlagReturn,
} from "./hooks/useFeatureFlag"

/**
 * Flag definition with metadata.
 */
export type FlagDefinition<T extends boolean | string | number> = {
  /** The type of value this flag returns */
  type: T extends boolean ? "boolean" : T extends string ? "string" : "number"
  /** Default value when flag is not configured or disabled */
  defaultValue: T
  /** Human-readable description */
  description: string
  /** Optional tags for categorization */
  tags?: string[]
}

/**
 * Central registry of all feature flags.
 *
 * Add new flags here to enable type-safe consumption.
 *
 * @example
 * ```typescript
 * export const FLAG_REGISTRY = {
 *   "new-dashboard": {
 *     type: "boolean",
 *     defaultValue: false,
 *     description: "Enable the new dashboard UI",
 *     tags: ["ui", "dashboard"],
 *   },
 *   "max-upload-size": {
 *     type: "number",
 *     defaultValue: 10,
 *     description: "Maximum file upload size in MB",
 *     tags: ["upload", "limits"],
 *   },
 * } as const satisfies FlagRegistry
 * ```
 */
export const FLAG_REGISTRY = {
  // UI Feature Flags
  "new-ui": {
    type: "boolean",
    defaultValue: false,
    description: "Enable new UI components and layouts",
    tags: ["ui", "redesign"],
  },

  "blueprint-import": {
    type: "boolean",
    defaultValue: true,
    description: "Enable legacy blueprint PDF import functionality (CL/UO format)",
    tags: ["import", "blueprints", "legacy"],
  },

  "dark-mode": {
    type: "boolean",
    defaultValue: false,
    description: "Enable dark mode and the theme selector in the application shell",
    tags: ["ui", "theme"],
  },

  internationalization: {
    type: "boolean",
    defaultValue: false,
    description: "Enable non-English UI languages and the language selector",
    tags: ["ui", "i18n"],
  },

  // Limits and Thresholds
  "bulk-operation-limit": {
    type: "number",
    defaultValue: 100,
    description: "Maximum number of items in a bulk operation",
    tags: ["limits", "bulk-operations"],
  },

  // Feature Variants
  "optimization-algorithm": {
    type: "string",
    defaultValue: "standard",
    description: "Which optimization algorithm to use (standard, enhanced, experimental)",
    tags: ["optimization", "algorithm"],
  },

  // Files Visibility
  "show-files": {
    type: "boolean",
    defaultValue: false,
    description: "Show the Files section, navigation links, and generate-all-files functionality",
    tags: ["ui", "files"],
  },

  // Project Management
  "project-status-presence": {
    type: "boolean",
    defaultValue: false,
    description: "Enable project status workflow and presence awareness features",
    tags: ["projects", "collaboration"],
  },

  // Fixture Detection Validation
  "fixture-detection-validation": {
    type: "boolean",
    defaultValue: false,
    description: "Enable the Fixture Detection Validation page in Settings",
    tags: ["ui", "validation"],
  },

  // Developer/Debug Features
  explanations: {
    type: "boolean",
    defaultValue: false,
    description: "Enable placement explanations feature in the optimization workflow",
    tags: ["debug", "developer"],
  },

  // Shelf-layout fill — routes "Fill store" through the backend project-shaped
  // shelf-layout endpoint. Flag name is retained from the earlier per-item
  // experiment.
  "e61-mg-predictor": {
    type: "boolean",
    defaultValue: false,
    description:
      "Route the 'Fill store' button through the backend shelf-layout fill endpoint instead of the legacy rule-based optimizer.",
    tags: ["optimization"],
  },

  // Canvas Controls
  "pan-mode": {
    type: "boolean",
    defaultValue: false,
    description: "Show pan mode toggle in optimize and process canvas views",
    tags: ["ui", "canvas"],
  },

  // Row Editor UI
  "row-editor-sidebar": {
    type: "boolean",
    defaultValue: false,
    description: "Show row editor in sidebar instead of floating drawer overlay",
    tags: ["ui", "optimization"],
  },

  // Layout Explorer
  "layout-explorer": {
    type: "boolean",
    defaultValue: false,
    description: "Show the Layout Explorer tab in the main navigation",
    tags: ["ui", "developer"],
  },

  "layout-explorer-change-requests": {
    type: "boolean",
    defaultValue: false,
    description:
      "Show the Layout Explorer structured change-request preview flow. Requires Layout Explorer to be enabled.",
    tags: ["ui", "developer", "layout-explorer"],
  },

  // Config Version Preview
  "config-preview": {
    type: "boolean",
    defaultValue: true,
    description:
      "Enable config version preview: compare differences and preview optimization results with a different config version without persisting changes",
    tags: ["ui", "optimization", "config"],
  },

  // MG placement stability
} as const satisfies FlagRegistry

/**
 * Type constraint for the flag registry.
 */
type FlagRegistry = Record<string, FlagDefinition<boolean | string | number>>

/**
 * Extract flag keys from the registry.
 */
export type FlagKey = keyof typeof FLAG_REGISTRY

/**
 * Extract the value type for a specific flag.
 */
export type FlagValue<K extends FlagKey> = (typeof FLAG_REGISTRY)[K]["defaultValue"]

/**
 * Type-safe feature flag hook with inferred return types.
 *
 * Uses the FLAG_REGISTRY to infer the correct value type.
 *
 * @param key - Flag key from the registry
 * @param options - Optional context overrides
 *
 * @example
 * ```tsx
 * // Boolean flag - value is typed as boolean
 * const { value: showNewUI } = useTypedFlag("new-ui")
 *
 * // Number flag - value is typed as number
 * const { value: limit } = useTypedFlag("bulk-operation-limit")
 *
 * // String flag - value is typed as string
 * const { value: algorithm } = useTypedFlag("optimization-algorithm")
 * ```
 */
export function useTypedFlag<K extends FlagKey>(
  key: K,
  options?: UseFeatureFlagOptions,
): UseFeatureFlagReturn<FlagValue<K>> {
  const flagDef = FLAG_REGISTRY[key]
  return useFeatureFlag(key, flagDef.defaultValue, options) as UseFeatureFlagReturn<FlagValue<K>>
}

/**
 * Get the default value for a flag from the registry.
 *
 * Useful for server-side code that needs default values without React hooks.
 */
export function getFlagDefault<K extends FlagKey>(key: K): FlagValue<K> {
  return FLAG_REGISTRY[key].defaultValue as FlagValue<K>
}

/**
 * Get flag metadata from the registry.
 */
export function getFlagDefinition<K extends FlagKey>(
  key: K,
): FlagDefinition<boolean | string | number> {
  return FLAG_REGISTRY[key]
}

/**
 * Get all flag keys from the registry.
 */
export function getAllFlagKeys(): FlagKey[] {
  return Object.keys(FLAG_REGISTRY) as FlagKey[]
}

/**
 * Get all flags matching a tag.
 */
export function getFlagsByTag(tag: string): FlagKey[] {
  return (
    Object.entries(FLAG_REGISTRY) as Array<[FlagKey, FlagDefinition<boolean | string | number>]>
  )
    .filter(([_, def]) => def.tags?.includes(tag))
    .map(([key]) => key)
}
